// ts-rest contract for the Clients tier — TerceroClient account umbrellas
// (three-tier client model Stage 3, ADR-0025 D2).
//
// READ-ONLY by design: TerceroClient is ingest-written (the ValuationGrid
// grouping pass); Septima users never create, edit or delete one (PRD §8),
// so this contract carries no mutations. A per-view presentation projection,
// NOT a domain model — the controller maps `ctx.read(TerceroClient)` (and
// the accounts beneath) onto these DTOs.
//
// The tier has NO pages of its own: the umbrella list is the tri-tier Client
// Book table, and a single umbrella's identity is surfaced on its client's
// Client Book record (`clientDetailSchema.umbrellas`). The one endpoint here
// backs the relationship counterpart picker's umbrella search.
//
// Endpoints under /api/clients:
//   GET /api/clients                    — umbrella directory (search + filter)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums (closed domain values — snake_case VALUES, camelCase keys)
// ---------------------------------------------------------------------------

/** `orphaned` = the umbrella's Client Reference disappeared from a later
 * grid upload; the row is kept (never deleted) and surfaced with a badge. */
export const terceroClientStatusSchema = z.enum(['active', 'orphaned']);
export type TerceroClientStatus = z.infer<typeof terceroClientStatusSchema>;

// ---------------------------------------------------------------------------
// Directory rows + stats
// ---------------------------------------------------------------------------

export const terceroClientRowSchema = z.object({
    terceroClientId: z.string(),
    /** The raw Client Reference, verbatim, for display. */
    clientReference: z.string(),
    /** "<prefix> <accountNumber>"; null for unparseable references. */
    parentCode: z.string().nullable(),
    /** The wrapper codes of the accounts beneath (D/I/JI/R/L/…), deduped and
     * sorted — a DI client shows its D + I portfolios' wrappers here. */
    wrappers: z.array(z.string()),
    /** The single linked person Client when the whole umbrella is one person;
     * null for joint/multi-owner or internal/unmatched umbrellas. Prefer
     * `involvedClients` for display — this stays for the clean single-owner
     * path and back-compatibility. */
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    /** Every distinct person Client the umbrella touches (from its accounts),
     * name-resolved within the caller's read scope (SEP-166). One entry for a
     * clean single-owner umbrella; several for a joint/multi-owner one — the
     * picker shows all parties instead of hiding the umbrella. `clientName` is
     * null when the person is outside the caller's read. */
    involvedClients: z.array(z.object({ clientId: z.string(), clientName: z.string().nullable() })),
    accountCount: z.number().int(),
    status: terceroClientStatusSchema,
    /** False for every row until the Suitability Record tier links (Stage 4);
     * backs the Linked / "No Suitability Record" filter chips. */
    hasSuitabilityRecord: z.boolean(),
});
export type TerceroClientRow = z.infer<typeof terceroClientRowSchema>;

export const terceroClientsStatsSchema = z.object({
    total: z.number().int(),
    /** Rows with a Suitability Record link — zero until Stage 4. */
    linked: z.number().int(),
    orphaned: z.number().int(),
    totalAccounts: z.number().int(),
});
export type TerceroClientsStats = z.infer<typeof terceroClientsStatsSchema>;

export const listTerceroClientsResponseSchema = z.object({
    rows: z.array(terceroClientRowSchema),
    stats: terceroClientsStatsSchema,
});
export type ListTerceroClientsResponse = z.infer<typeof listTerceroClientsResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const clientsContract = c.router(
    {
        listTerceroClients: {
            method: 'GET',
            path: '/clients',
            query: z
                .object({
                    search: z.string().optional(),
                    status: terceroClientStatusSchema.optional(),
                })
                .optional(),
            responses: { 200: listTerceroClientsResponseSchema },
            summary: 'TerceroClient umbrella directory (read-only, ingest-derived)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
